import { apiService } from './api';
import { Product, ProductCategory, PaginationParams } from '../types';

export interface CreateProductRequest {
  // sku is auto-generated by the server — not sent from the client
  name: string;
  description: string;
  categoryId: number;
  unitOfMeasure: string;
  reorderLevel: number;
  reorderQuantity: number;
  attributeSetId?: number | null;
}

export interface CreateSalesItemPayload {
  sellingPrice: number;
  minimumSellingPrice?: number;
  salesCurrency: string;
  salesChannel: string;
  discountAllowed: boolean;
  returnAllowed: boolean;
  returnPeriodDays?: number;
  salesTaxId?: number;
}

export interface CreatePurchaseItemPayload {
  supplierId: number;
  purchasePrice: number;
  purchaseCurrency: string;
  leadTimeDays: number;
  minimumOrderQuantity: number;
  supplierItemCode: string;
  purchaseTaxId?: number;
}

export interface CreateProductWithItemsRequest {
  // Product
  name: string;
  description?: string;
  categoryId?: number;
  unitOfMeasure: string;
  reorderLevel: number;
  reorderQuantity: number;
  attributeSetId?: number | null;
  attributeValues?: { attributeDefinitionId: number; value: string }[];
  // Null/omitted = don't create that item
  salesItem?: CreateSalesItemPayload;
  purchaseItem?: CreatePurchaseItemPayload;
}

export interface UpdateProductRequest {
  name: string;
  description: string;
  categoryId: number;
  unitOfMeasure: string;
  reorderLevel: number;
  reorderQuantity: number;
  attributeSetId?: number | null;
}

export interface CreateCategoryRequest {
  name: string;
  description?: string;
  parentCategoryId?: number;
  isActive: boolean;
  defaultInventoryAccountId?: number | null;
  defaultCOGSAccountId?: number | null;
  defaultRevenueAccountId?: number | null;
}

export interface UpdateCategoryRequest extends CreateCategoryRequest {}

const PRODUCT_API = '/products';
const CATEGORIES_API = '/products/categories';
const TAXES_API = '/taxes';
const UNITS_API = '/units';
const UNIT_CONVERSIONS_API = '/unit-conversions';
const PURCHASE_ITEMS_API = '/products/purchase-items';
const SALES_ITEMS_API = '/products/sales-items';
const WAREHOUSES_API = '/organization/warehouses';
const CUSTOMERS_API = '/customers';
const SUPPLIERS_API = '/suppliers';

export const productService = {
  // Products
  getProducts: (params: PaginationParams) =>
    apiService.get<Product[]>(PRODUCT_API, {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 50,
    }),

  getProductById: (id: number) =>
    apiService.get<Product>(`${PRODUCT_API}/${id}`),

  getProductBySku: (sku: string) =>
    apiService.get<Product>(`${PRODUCT_API}/sku/${sku}`),

  createProduct: (data: CreateProductRequest) =>
    apiService.post<Product>(PRODUCT_API, data),

  createProductWithItems: (data: CreateProductWithItemsRequest) =>
    apiService.post<any>(`${PRODUCT_API}/with-items`, data),

  updateProduct: (id: number, data: UpdateProductRequest) =>
    apiService.put<Product>(`${PRODUCT_API}/${id}`, data),

  deleteProduct: (id: number) =>
    apiService.delete<void>(`${PRODUCT_API}/${id}`),

  // Categories
  getCategories: (params?: PaginationParams) =>
    apiService.get<ProductCategory[]>(CATEGORIES_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  getCategoryById: (id: number) =>
    apiService.get<ProductCategory>(`${CATEGORIES_API}/${id}`),

  createCategory: (data: CreateCategoryRequest) =>
    apiService.post<ProductCategory>(CATEGORIES_API, data),

  updateCategory: (id: number, data: UpdateCategoryRequest) =>
    apiService.put<ProductCategory>(`${CATEGORIES_API}/${id}`, data),

  deleteCategory: (id: number) =>
    apiService.delete<void>(`${CATEGORIES_API}/${id}`),

  // Taxes
  getTaxes: (params?: PaginationParams) =>
    apiService.get<any[]>(TAXES_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  getTaxById: (id: number) =>
    apiService.get<any>(`${TAXES_API}/${id}`),

  createTax: (data: any) =>
    apiService.post<any>(TAXES_API, data),

  updateTax: (id: number, data: any) =>
    apiService.put<any>(`${TAXES_API}/${id}`, data),

  deleteTax: (id: number) =>
    apiService.delete<void>(`${TAXES_API}/${id}`),

  // Units
  getUnits: (params?: PaginationParams) =>
    apiService.get<any[]>(UNITS_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  getUnitById: (id: number) =>
    apiService.get<any>(`${UNITS_API}/${id}`),

  createUnit: (data: any) =>
    apiService.post<any>(UNITS_API, data),

  updateUnit: (id: number, data: any) =>
    apiService.put<any>(`${UNITS_API}/${id}`, data),

  deleteUnit: (id: number) =>
    apiService.delete<void>(`${UNITS_API}/${id}`),

  // Unit Conversions
  getUnitConversions: (params?: PaginationParams) =>
    apiService.get<any[]>(UNIT_CONVERSIONS_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  createUnitConversion: (data: any) =>
    apiService.post<any>(UNIT_CONVERSIONS_API, data),

  updateUnitConversion: (id: number, data: any) =>
    apiService.put<any>(`${UNIT_CONVERSIONS_API}/${id}`, data),

  deleteUnitConversion: (id: number) =>
    apiService.delete<void>(`${UNIT_CONVERSIONS_API}/${id}`),

  // Purchase Items
  getPurchaseItems: (params?: PaginationParams) =>
    apiService.get<any[]>(PURCHASE_ITEMS_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  createPurchaseItem: (data: any) =>
    apiService.post<any>(PURCHASE_ITEMS_API, data),

  // Sales Items
  getSalesItems: (params?: PaginationParams) =>
    apiService.get<any[]>(SALES_ITEMS_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  createSalesItem: (data: any) =>
    apiService.post<any>(SALES_ITEMS_API, data),

  // Warehouses
  getWarehouses: (params?: PaginationParams) =>
    apiService.get<any[]>(WAREHOUSES_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  createWarehouse: (data: any) =>
    apiService.post<any>(WAREHOUSES_API, data),

  updateWarehouse: (id: number, data: any) =>
    apiService.put<any>(`${WAREHOUSES_API}/${id}`, data),

  deleteWarehouse: (id: number) =>
    apiService.delete<void>(`${WAREHOUSES_API}/${id}`),

  // Customers
  getCustomers: (params?: PaginationParams) =>
    apiService.get<any[]>(CUSTOMERS_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  createCustomer: (data: any) =>
    apiService.post<any>(CUSTOMERS_API, data),

  // Suppliers
  getSuppliers: (params?: PaginationParams) =>
    apiService.get<any[]>(SUPPLIERS_API, {
      pageNumber: params?.pageNumber || 1,
      pageSize: params?.pageSize || 100,
    }),

  createSupplier: (data: any) =>
    apiService.post<any>(SUPPLIERS_API, data),
};
